import { ApiError } from '@/api/client';

const MESSAGES: Record<string, string> = {
  insufficient_balance: 'Saldo insuficiente para esta recarga',
  already_has_active_session: 'Você já tem uma recarga em andamento',
  hub_rejected_start: 'O carregador rejeitou o início. Tente em alguns segundos.',
  charging_session_not_found: 'Sessão não encontrada',
  amount_out_of_range: 'Valor fora do intervalo permitido',
  balance_cap_exceeded: 'Saldo máximo atingido (R$ 2.000)',
  invalid_credentials: 'Email ou senha inválidos',
  unauthorized: 'Sessão expirada — faça login novamente',
  email_taken: 'Este email já está cadastrado',
  cpf_taken: 'Este CPF já está cadastrado',
  invalid_cpf: 'CPF inválido',
  weak_password: 'Senha fraca. Use 8+ caracteres com letra maiúscula e número.',
  google_only_account:
    'Esta conta ainda não tem senha. Use "Continuar com Google" ou crie uma senha em "Esqueci minha senha".',
  connector_unavailable: 'Conector indisponível no momento',
  // Duas carteiras (migration 049)
  wallet_in_debt: 'Recarga bloqueada — adicione saldo pra cobrir o valor pendente.',
  bonus_insufficient: 'Cashback insuficiente pra iniciar. Use o saldo.',
  amount_below_debt: 'O valor precisa cobrir o pendente do seu saldo.',
  hub_client_not_ready: 'Serviço temporariamente indisponível. Tente novamente.',
  session_not_active: 'Sessão já encerrada',
  // O Hub não confirmou que o RemoteStop chegou ao carregador (Rejected,
  // timeout do BLPOP, ou `mock=true`). A sessão CONTINUA ativa de propósito e
  // segue cobrando o consumo real — o cliente precisa saber que o carro pode
  // estar carregando ainda e que parar pelo carro encerra (no DC o cabo não
  // solta com corrente passando — docs/bugs/2026-09-24-encerrar-engolido-cache-relay.md).
  // Ver docs/bugs/2026-08-22-stop-rejected-ignorado-subcobranca.md.
  stop_not_confirmed:
    'Não conseguimos parar o carregador à distância — o carro pode continuar carregando. Pare a recarga pelo carro (destrave pela chave ou pelo app do carro) e depois retire o cabo. A cobrança segue o consumo real.',
  // O charger desistiu (~60s) porque o cabo nunca foi plugado depois do start.
  // Mensagem aparece na ActiveCharge via WS quando o Hub emite
  // remote_start.rejected → end_reason=charger_timeout_no_plug.
  charger_timeout_no_plug: 'O cabo não foi plugado a tempo. Plugue o cabo no carro primeiro e toque em Iniciar novamente.',
  // "Sem plug" em segundos (KI-040 do Hub) e conector em falha no start.
  // `connector_cooldown` fica de fora de propósito: o backend manda a
  // mensagem com os segundos que faltam ("Aguarde 37s…").
  charger_start_unconfirmed:
    'O carregador não confirmou o início. Aguarde 1 minuto com o cabo plugado e tente de novo.',
  charger_fault:
    'O carregador ou o carro recusou o início. Tente de novo ou use o outro conector.',
  invalid_reset_token: 'Link de recuperação inválido ou expirado.',
  token_expired: 'Seu link expirou. Solicite um novo.',
  invalid_email_change_token: 'Link de confirmação inválido ou expirado.',
  email_unchanged: 'O novo email é igual ao atual.',
  // Indique e Ganhe. `referral_not_eligible` fica de fora de propósito: o
  // backend manda a mensagem específica (já carrega / convite mútuo / teto).
  referral_disabled: 'O programa de indicação não está disponível no momento.',
  referral_code_invalid: 'Código de indicação não encontrado. Confira com quem te indicou.',
  referral_already_claimed: 'Você já usou um código de indicação.',
  referral_self: 'Esse é o seu próprio código — compartilhe com um amigo.',
};

/** Padrões de razão de rejeição do Hub Gateway (resp.reason vem em
 *  `err.message`). Mapeia palavras-chave do Hub pra mensagens explicativas
 *  pro motorista, em vez do genérico "carregador rejeitou".
 *  Veja `bow-backend/docs/bugs/2026-05-28-hub-single-session-mock.md`. */
const HUB_REJECTION_REASONS: Array<[RegExp, string]> = [
  // RACE_CONDITION_LOCK_HELD, station_busy, already_active_session, occupied, in_use…
  [
    /race[\s_-]*condition|lock[\s_-]*held|station[\s_-]*busy|already[\s_-]*active|occupied|in[\s_-]*use/i,
    'Esta estação já tem outro cabo em uso. Aguarde encerrar ou troque de ponto.'
  ],
  // Charger off-line / offline / unavailable
  [
    /off[\s_-]*line|unavailable|not[\s_-]*reachable/i,
    'O carregador está sem comunicação. Tente em alguns minutos ou fale com o suporte.'
  ]
];

function hubRejectedFriendly(reason: string): string {
  for (const [pattern, msg] of HUB_REJECTION_REASONS) {
    if (pattern.test(reason)) return msg;
  }
  return MESSAGES.hub_rejected_start;
}

export function friendlyMessage(err: unknown, fallback = 'Erro inesperado'): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      return 'Muitas tentativas. Aguarde um momento e tente novamente.';
    }
    // Hub Gateway repassa a razão específica em `err.message` (resp.reason).
    // Pra `hub_rejected_start` quebramos por causa pra dar uma resposta útil.
    if (err.code === 'hub_rejected_start') {
      return hubRejectedFriendly(err.message ?? '');
    }
    return MESSAGES[err.code] ?? err.message ?? fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
